import * as React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import { Controller, useForm } from "react-hook-form";
import { z as zod } from "zod";

import { Account, useCreateAccount, useUpdateAccount } from "@/hooks/api/use-accounts";
import { CustomDrawer } from "@/components/custom-drawer";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";

export interface CreateAccountProps {
	account: Account | null | undefined;
	isCreateDrawerOpen: boolean;
	onCreateDrawerClose: () => void;
}

const schema = zod.object({
	// firstName: zod.string().min(1, "First name is required"),
	// lastName: zod.string().min(1, "Last name is required"),
	// companyName: zod.string().min(1, "Company name is required"),
	// email: zod.string().email("Must be a valid email").min(1, "Email is required"),
	// status: zod.string().min(1, "Status is required"),
	name: zod.string().min(1, "Name is required"),
	type: zod.enum(["enterprise", "smb"]),
});

type Values = zod.infer<typeof schema>;

export function CreateAccount({
	account,
	isCreateDrawerOpen,
	onCreateDrawerClose,
}: CreateAccountProps): React.JSX.Element {
	const isEdit = !!account;

	const createAccountMutation = useCreateAccount();
	const updateAccountMutation = useUpdateAccount();

	const defaultValues = {
		// firstName: account?.firstName || "",
		// lastName: account?.lastName || "",
		// companyName: account?.companyName || "",
		// email: account?.email || "",
		// status: account?.status || "",
		name: account?.name || "",
		type: account?.type || "enterprise",
	} satisfies Values;

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
		// watch,
		setError,
	} = useForm<Values>({
		defaultValues,
		resolver: zodResolver(schema),
	});

	// const status = watch("status");

	React.useLayoutEffect(() => {
		reset({
			name: account?.name || "",
			type: account?.type || "enterprise",
		});
	}, [reset, account, isCreateDrawerOpen]);

	const onSubmitForm = async (data: Values) => {
		try {
			await (isEdit && account?.id
				? updateAccountMutation.mutateAsync({ id: account.id, data: { name: data.name } })
				: createAccountMutation.mutateAsync(data));

			onCreateDrawerClose();
		} catch (error: any) {
			const errorMessages = error?.response?.data?.message;

			if (!Array.isArray(errorMessages)) {
				setError("root", { message: errorMessages });
				return;
			}

			const errorMessage = errorMessages[0];
			setError(errorMessage.split(" ")[0], { message: errorMessage });
		}
	};

	return (
		<CustomDrawer
			open={isCreateDrawerOpen}
			onClose={onCreateDrawerClose}
			title={isEdit ? "Edit Account" : "Add Account"}
			onSubmit={handleSubmit(onSubmitForm)}
			isSubmitButtonDisabled={isSubmitting || createAccountMutation.isPending || updateAccountMutation.isPending}
			submitButtonText={isEdit ? "Update Account" : "Add Account"}
      width="420px"
		>
			<form onSubmit={handleSubmit(onSubmitForm)}>
				<Grid container spacing={2}>
					{/* <Grid
							size={{
								md: 6,
								xs: 12,
							}}
						>
							<Controller
								control={control}
								name="firstName"
								render={({ field }) => (
									<FormControl error={Boolean(errors.firstName)} fullWidth>
										<InputLabel>First name</InputLabel>
										<OutlinedInput {...field} placeholder="Enter first name" />
										{errors.firstName ? <CustomFormHelperError text={errors.firstName.message || ""} /> : null}
									</FormControl>
								)}
							/>
						</Grid>
						<Grid
							size={{
								md: 6,
								xs: 12,
							}}
						>
							<Controller
								control={control}
								name="lastName"
								render={({ field }) => (
									<FormControl error={Boolean(errors.lastName)} fullWidth>
										<InputLabel>Last name</InputLabel>
										<OutlinedInput {...field} placeholder="Enter last name" />
										{errors.lastName ? <CustomFormHelperError text={errors.lastName.message || ""} /> : null}
									</FormControl>
								)}
							/>
						</Grid>
						<Grid
							size={{
								md: 12,
								xs: 12,
							}}
						>
							<Controller
								control={control}
								name="email"
								render={({ field }) => (
									<FormControl error={Boolean(errors.email)} fullWidth>
										<InputLabel>Email address</InputLabel>
										<OutlinedInput {...field} type="email" placeholder="Enter email address" />
										{errors.email ? <CustomFormHelperError text={errors.email.message || ""} /> : null}
									</FormControl>
								)}
							/>
						</Grid>
						<Grid
							size={{
								md: 6,
								xs: 12,
							}}
						>
							<Controller
								control={control}
								name="companyName"
								render={({ field }) => (
									<FormControl error={Boolean(errors.companyName)} fullWidth>
										<InputLabel>Company name</InputLabel>
										<OutlinedInput {...field} placeholder="Enter company name" />
										{errors.companyName ? <CustomFormHelperError text={errors.companyName.message || ""} /> : null}
									</FormControl>
								)}
							/>
						</Grid>
						<Grid
							size={{
								md: 6,
								xs: 12,
							}}
						>
							<Controller
								control={control}
								name="status"
								render={({ field }) => (
									<FormControl error={Boolean(errors.status)} fullWidth>
										<InputLabel>Status</InputLabel>
										<Select {...field} sx={{ color: status === "" ? "var(--mui-palette-text-secondary)" : "inherit" }}>
											<Option value="" disabled>
												Select a status
											</Option>
											<Option value="active">Active</Option>
											<Option value="inactive">Inactive</Option>
										</Select>
										{errors.status ? <CustomFormHelperError text={errors.status.message || ""} /> : null}
									</FormControl>
								)}
							/>
						</Grid> */}
					<Grid
						size={{
							md: 12,
							xs: 12,
						}}
					>
						<Controller
							control={control}
							name="name"
							render={({ field }) => (
								<FormControl error={Boolean(errors.name)} fullWidth>
									<InputLabel>Name</InputLabel>
									<OutlinedInput {...field} placeholder="Enter name" />
									{errors.name ? <CustomFormHelperError text={errors.name.message || ""} /> : null}
								</FormControl>
							)}
						/>
					</Grid>
					<Grid
						size={{
							md: 12,
							xs: 12,
						}}
					>
						<Controller
							control={control}
							name="type"
							render={({ field }) => (
								// The kind decides the owner's role, billing and onboarding, so it is set once.
								<FormControl disabled={isEdit} fullWidth>
									<InputLabel>Type</InputLabel>
									<Select {...field}>
										<MenuItem value="enterprise">Enterprise</MenuItem>
										<MenuItem value="smb">SMB</MenuItem>
									</Select>
								</FormControl>
							)}
						/>
					</Grid>
				</Grid>
			</form>
		</CustomDrawer>
	);
}
